/**
 * Step Navigation Hook
 * 
 * Handles step navigation with validation
 */

import { useState, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { JobPostFormData } from '../schemas/jobPostValidation';

// Field names for each step
const STEP_FIELDS: Record<number, (keyof JobPostFormData)[]> = {
  0: ['step0'],
  1: ['step1'],
  2: ['step2'],
  3: ['step3'],
  4: ['step4'],
  5: ['step5'],
  6: ['step6'],
};

export const useStepNavigation = (
  form: UseFormReturn<JobPostFormData>,
  totalSteps: number = 7
) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isNavigating, setIsNavigating] = useState(false);

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    const fields = STEP_FIELDS[currentStep];
    if (!fields || fields.length === 0) {
      return true; // Step 0 and Step 6 don't need validation
    }

    try {
      const isValid = await form.trigger(fields);
      return isValid;
    } catch (error) {
      console.error('Validation error:', error);
      return false;
    }
  }, [form, currentStep]);

  const goToStep = useCallback(
    async (step: number) => {
      if (step < 0 || step >= totalSteps || step === currentStep) {
        return;
      }

      setIsNavigating(true);

      try {
        // Allow navigation without blocking - validation will be shown in stepper
        // Update current step in form data
        form.setValue('currentStep', step);
        setCurrentStep(step);
      } catch (error) {
        console.error('Error navigating to step:', error);
      } finally {
        setIsNavigating(false);
      }
    },
    [currentStep, totalSteps, form, validateCurrentStep]
  );

  const goToNextStep = useCallback(async () => {
    if (currentStep < totalSteps - 1) {
      await goToStep(currentStep + 1);
    }
  }, [currentStep, totalSteps, goToStep]);

  const goToPreviousStep = useCallback(async () => {
    if (currentStep > 0) {
      await goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  return {
    currentStep,
    goToStep,
    goToNextStep,
    goToPreviousStep,
    isNavigating,
    canGoNext: currentStep < totalSteps - 1,
    canGoPrevious: currentStep > 0,
  };
};


